// V2 CSV parser: groups by (core_id, adset_category) for verdict engine
import Papa from 'papaparse';
import { isCreatorAd } from '../creatorType';

export function parseCSV(file) {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const ads = results.data.map(transformRow).filter(Boolean);
        resolve(ads);
      },
      error: reject,
    });
  });
}

function normalize(s) {
  return (s || '').toLowerCase().replace(/[\u2019\u2018]/g, "'");
}

function findCol(row, keywords, opts = {}) {
  const cols = Object.keys(row);
  const exclude = opts.exclude || [];
  for (const kw of keywords) {
    const nkw = normalize(kw);
    const found = cols.find(c => {
      const nc = normalize(c);
      if (!nc.includes(nkw)) return false;
      if (exclude.some(e => nc.includes(normalize(e)))) return false;
      return true;
    });
    if (found) return found;
  }
  return null;
}

let _colCache = null;
function detectColumns(row) {
  if (_colCache) return _colCache;
  _colCache = {
    adName:    findCol(row, ['Nom de la publicité', 'Ad name']),
    adsetName: findCol(row, ["Nom de l'ensemble", 'Ad set name']),
    delivery:  findCol(row, ['Diffusion des publicités', 'Ad delivery']),
    results:   findCol(row, ['Résultats', 'Results']),
    cpaRaw:    findCol(row, ['Coût par résultat', 'Cost per result']),
    spend:     findCol(row, ['Montant dépensé', 'Amount spent']),
    impressions: findCol(row, ['Impressions']),
    reach:     findCol(row, ['Couverture', 'Reach']),
    dateStart: findCol(row, ['Début des rapports', 'Day', 'Date start']),
    dateEnd:   findCol(row, ['Fin des rapports', 'Date end']),
  };
  return _colCache;
}

function toNum(v) {
  if (v === null || v === undefined || v === '') return 0;
  return parseFloat(String(v).replace(/\s/g, '').replace(',', '.')) || 0;
}

function transformRow(row) {
  const cols = detectColumns(row);
  const adName = row[cols.adName];
  const adsetName = row[cols.adsetName];
  if (!adName) return null;
  return {
    ad_name: adName,
    adset_name: adsetName || '',
    delivery: row[cols.delivery] || '',
    results: toNum(row[cols.results]),
    cpa_raw: toNum(row[cols.cpaRaw]) || null,
    spend: toNum(row[cols.spend]),
    impressions: toNum(row[cols.impressions]),
    reach: toNum(row[cols.reach]),
    date_start: row[cols.dateStart] || '',
    date_end: row[cols.dateEnd] || '',
    core_id: extractCoreId(adName),
    adset_category: categorizeAdset(adsetName),
    ad_owner: categorizeOwner(adName),
  };
}

export function extractCoreId(adName) {
  const match = adName?.match(/^(\d+)_/);
  return match ? match[1] : null;
}

export function categorizeAdset(adsetName) {
  if (!adsetName) return 'UNKNOWN';
  const n = adsetName.toUpperCase();
  // v4.00 — TEST gagne sur tout autre token. Les ad sets de test PhotoRoom portent
  // le mot-clé universel TEST (souvent WEEKLY_TEST) même préfixés BIS_/ITER_/etc.
  // Ex. "BIS_WEEKLY_TEST_INFLUENCER_ANIM_[EN]" est un TEST, pas un BIS de scaling.
  if (n.includes('TEST')) return 'TEST';
  if (n.includes('BAU') || n.includes('[FEATURE]')) return 'MAIN';
  if (n.includes('ITERATION')) return 'ITERATION';
  if (n.startsWith('[BIS]') || n.includes('BIS_')) return 'BIS';
  return 'OTHER';
}

export function categorizeOwner(adName) {
  if (!adName) return 'UNKNOWN';
  // PhotoRoom convention: {coreId}_{marketer}_influencer-{INFLUENCER}_..._
  // Matches yann_influencer-, houda_influencer-, jeremy_influencer-, etc.
  if (isCreatorAd(adName)) return 'influencer';
  const n = adName.toLowerCase();
  if (n.includes('yann_internal')) return 'YANN_INTERNAL';
  if (n.includes('yann_freelancer')) return 'YANN_FREELANCER';
  if (n.includes('_internal_')) return 'INHOUSE_INTERNAL';
  return 'OTHER';
}

export function aggregateByCorIdAndCategory(rows) {
  _colCache = null; // reset for next parse
  const map = new Map();
  for (const row of rows) {
    if (!row.core_id) continue;
    // v2: a test run IS an adset (Yann duplicates the adset and changes the date).
    // For the TEST category only, add the adset to the grouping key (adset_id if present,
    // otherwise adset_name) so a test and its retest stay separate rows and the
    // ">=5 purchases per run" threshold is measured per run. Other categories keep their key.
    const key = row.adset_category === 'TEST'
      ? `${row.core_id}::TEST::${row.adset_id || row.adset_name || ''}`
      : `${row.core_id}::${row.adset_category}`;
    if (!map.has(key)) {
      map.set(key, {
        core_id: row.core_id, adset_category: row.adset_category, ad_owner: row.ad_owner,
        ad_name_sample: row.ad_name, adset_name_sample: row.adset_name, delivery: row.delivery,
        thumbnail_url: row.thumbnail_url || null,
        ad_id: row.ad_id || null,
        achats: 0, spend: 0, impressions: 0, reach: 0,
        // v4.02 — leading indicators accumulators. CSV path doesn't read them
        // (transformRow ignores these columns), so they stay 0 there — graceful
        // degradation. Live Meta path populates them via transformMetaRow.
        clicks: 0, video_p25: 0, video_p100: 0,
        first_date: null, last_date: null,
      });
    }
    const agg = map.get(key);
    if (!agg.thumbnail_url && row.thumbnail_url) agg.thumbnail_url = row.thumbnail_url;
    // Prefer the ad_id of the most RECENT active row (old ids get archived → Meta shows the adset,
    // dead video source). Freshest active ad_id => best for playback, thumbnail proxy and Meta link.
    if (row.ad_id && ((row.spend && row.spend > 0) || (row.results && row.results > 0))) {
      const d = row.date_end || row.date_start || '';
      if (!agg._adIdDate || d >= agg._adIdDate) { agg.ad_id = row.ad_id; agg._adIdDate = d; agg.thumbnail_url = row.thumbnail_url || agg.thumbnail_url; }
    }
    agg.achats += row.results;
    agg.spend += row.spend;
    agg.impressions += row.impressions;
    agg.reach += row.reach;
    agg.clicks += (row.clicks || 0);
    agg.video_p25 += (row.video_p25 || 0);
    agg.video_p100 += (row.video_p100 || 0);
    // FIX v3.92.4 : ne tracker les dates QUE si la ligne a de l'activité réelle
    // Sinon Meta exporte chaque ad pour tous les jours (même inactifs) et écrase first/last_date
    const hasActivity = (row.spend && row.spend > 0) || (row.results && row.results > 0);
    if (hasActivity) {
      if (row.date_start && (!agg.first_date || row.date_start < agg.first_date)) {
        agg.first_date = row.date_start;
      }
      if (row.date_end && (!agg.last_date || row.date_end > agg.last_date)) {
        agg.last_date = row.date_end;
      }
    }
  }
  return Array.from(map.values()).map(agg => ({
    ...agg,
    cpa: agg.achats > 0 ? agg.spend / agg.achats : null,
    ctr: agg.impressions > 0 ? (agg.clicks / agg.impressions) * 100 : null,
  }));
}
